<template>
  <div>
    <!-- Level 3 (primary axis) — ad targeting category -->
    <f7-block-title>{{ $t('adTargeting.segments') }}</f7-block-title>
    <div v-for="grp in AD_TARGETING_CATEGORIES" :key="grp.group" class="cat-section">
      <div class="cat-section-label">{{ grp.group }}</div>
      <div class="chip-row">
        <f7-link
          v-for="item in grp.items" :key="item.code"
          class="chip"
          :class="{ selected: modelValue.adTargetingCategories.includes(item.code) }"
          @click="toggle('adTargetingCategories', item.code)"
          role="button"
          tabindex="0"
          @keydown.enter="toggle('adTargetingCategories', item.code)"
          @keydown.space.prevent="toggle('adTargetingCategories', item.code)"
        >
          {{ item.label }}
        </f7-link>
      </div>
    </div>

    <!-- Level 1 — group category (optional refinement) -->
    <f7-block-title>{{ $t('adTargeting.refineGroupCat') }}</f7-block-title>
    <div class="chip-row">
      <f7-link
        v-for="cat in GROUP_CATEGORIES" :key="cat.code"
        class="chip"
        :class="{ selected: modelValue.groupCategories.includes(cat.code) }"
        @click="toggle('groupCategories', cat.code)"
        role="button"
        tabindex="0"
        @keydown.enter="toggle('groupCategories', cat.code)"
        @keydown.space.prevent="toggle('groupCategories', cat.code)"
      >
        {{ cat.label }}
      </f7-link>
    </div>

    <!-- Geo + size + event window -->
    <f7-block-title>{{ $t('adTargeting.reach') }}</f7-block-title>
    <f7-list inset strong media-list outline dividers>
      <f7-list-input :label="$t('adTargeting.citiesLabel')" type="text"
        :value="citiesInput" @input="onCitiesInput($event.target.value)" />
      <f7-list-input :label="$t('adTargeting.minGroupSize')" type="number"
        :value="modelValue.groupMinMembers" @input="modelValue.groupMinMembers = $event.target.value" />
      <f7-list-input :label="$t('adTargeting.eventDays')" type="number"
        :value="modelValue.eventWithinDays" @input="modelValue.eventWithinDays = $event.target.value" />
    </f7-list>

    <div class="audience-banner" v-if="hasAnySelection">
      <f7-icon f7="chart_bar_fill" :size="15" />
      <span>
        {{ $t('adTargeting.activeSegments', { count: modelValue.adTargetingCategories.length }) }}
        <span v-if="modelValue.groupCategories.length">, {{ $t('adTargeting.restrictedCategories', { count: modelValue.groupCategories.length }) }}</span>.
        {{ $t('adTargeting.audienceEstimate') }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { AD_TARGETING_CATEGORIES } from '../../constants/ad-targeting-categories.js'
import { GROUP_CATEGORIES } from '../../constants/group-taxonomy.js'
import i18n from '@/js/i18n.js'

const props = defineProps({ modelValue: { type: Object, required: true } })
const { t } = i18n.global

function toggle(field, code) {
  const list = props.modelValue[field]
  const idx = list.indexOf(code)
  if (idx >= 0) list.splice(idx, 1)
  else list.push(code)
}

const citiesInput = computed(() => (props.modelValue.cities || []).join(', '))
function onCitiesInput(val) {
  props.modelValue.cities = val.split(',').map(s => s.trim()).filter(Boolean)
}

const hasAnySelection = computed(() =>
  props.modelValue.adTargetingCategories.length > 0)
</script>

<style scoped>
.cat-section { margin-bottom: 10px; }
.cat-section-label { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); margin-bottom: 5px; }
.chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  padding: 5px 11px; border-radius: 12px; font-size: 11.5px;
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  text-decoration: none;
  color: inherit;
}
.chip.selected {
  border: 1.5px solid var(--f7-theme-color);
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  color: var(--f7-theme-color); font-weight: 500;
}
.audience-banner {
  display: flex; gap: 8px; align-items: flex-start;
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
  color: var(--f7-theme-color); border-radius: 8px; padding: 10px 12px;
  font-size: 12px; line-height: 1.5; margin: 10px 16px 0;
}
</style>
